import {useState, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {
  deletePlan,
  getAllPlans,
  getSubscriptionById,
  updatePlanStatus,
} from '../api/services';
import SuperAddPackage from './SuperAddPackage';
import useModal from '../hooks/useModal';
import api from '../Interceptor';

const SUPER_ADMIN = process.env.REACT_APP_SUPERADMIN_ROLE_ID;

const SuperPackages = () => {
  const user = useSelector((state) => state.auth.user);
  const {isOpen, toggleModal} = useModal();
  const [packages, setPackages] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subScriptionStatus, setSubScriptionStatus] =
    useState('Subscribe Plan');
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    fetchPackages();
    if (user?.userRole._id !== SUPER_ADMIN) {
      fetchCurrentSubscription();
    }
  }, [user]);

  const fetchPackages = async () => {
    try {
      const data = await getAllPlans();
      setPackages(data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      // const data = await getCurrentSubscriptionAp();
      // setCurrentSubscription(data.subscription);
    } catch (error) {
      // It's okay if no subscription exists
      // setCurrentSubscription(null);
    }
  };

  const handleStatusUpdate = async (id, isActive) => {
    try {
      await updatePlanStatus(id, isActive);
      await fetchPackages();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?'))
      return;

    try {
      await deletePlan(id);
      await fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
    }
  };

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const response = await api.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/v1/admin/payment/create`,
        {planId: planId}
      );
      const {url} = response.data;

      if (url) {
        window.location.href = url; // redirect to Safepay checkout
      } else {
        toast.error('Payment URL not received');
      }
    } catch (err) {
      toast.error('Payment creation failed');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (user?.userRole?.name === 'admin')
      getSubscriptionById().then((state) => {
        setSubScriptionStatus(state?.subscription);
      });
  }, []);

  const hasActiveSubscription =
    currentSubscription && currentSubscription.status === 'active';

  return (
    <div className="min-h-screen flex flex-col p-3 gap-2 overflow-hidden font-customFont">
      <div className="flex justify-between items-center">
        <div style={{fontFamily: 'Noto Serif, sans-serif'}}>
          <h1 className="font-bold text-lg md:text-xl">Packages</h1>
          <p className="text-xs text-gray-500 font-semibold">
            View and manage packages
          </p>
        </div>
        {user?.userRole._id === SUPER_ADMIN && (
          <div className="flex gap-3 items-center mt-2 md:mt-0">
            <button
              className="bg-themeGradient text-white px-2 py-1 rounded-md mb-4 md:mb-0 flex items-center"
              onClick={() => {
                setSelectedPkg(null);
                toggleModal();
              }}
            >
              <i className="fa-solid fa-plus text-lg"></i>
              <span className="ms-2">New Package</span>
            </button>
          </div>
        )}
      </div>

      {isOpen && (
        <SuperAddPackage
          isOpen={isOpen}
          onCancel={toggleModal}
          pkg={selectedPkg}
          onSuccess={() => {
            toggleModal();
            fetchPackages();
          }}
        />
      )}

      {/* Display current subscription info for admins */}
      {user?.userRole._id !== SUPER_ADMIN && currentSubscription && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800">Current Subscription</h3>
          <p className="text-sm text-blue-600">
            Plan: {currentSubscription.packageId?.name} | Status:{' '}
            <span
              className={
                currentSubscription.status === 'active'
                  ? 'text-green-600'
                  : 'text-orange-600'
              }
            >
              {currentSubscription.status}
            </span>{' '}
            | Expires:{' '}
            {new Date(currentSubscription.endDate).toLocaleDateString()}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
        {packages.map((pkg) => (
          <div
            key={pkg._id}
            className="group relative overflow-hidden rounded-2xl backdrop-blur-sm bg-white/80 
             border border-gray-100 hover:border-gray-300 shadow-md p-4 
             transition-all duration-500"
          >
            <div className="relative flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div
                    className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-600 
                        rounded-xl blur-lg opacity-40 group-hover:opacity-60 
                        transition-opacity duration-300"
                  />
                  <div
                    className="relative w-10 h-10 bg-gradient-to-br from-indigo-50 to-purple-100 
                        border-2 border-white shadow-sm flex items-center justify-center 
                        rounded-xl"
                  >
                    <i className="fa-solid fa-box text-indigo-700 text-lg"></i>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{pkg.name}</h3>
                  <p className="text-sm text-gray-500">${pkg.price} / month</p>
                </div>
              </div>
            </div>
            {user?.userRole._id === SUPER_ADMIN && (
              <div className="flex justify-between items-center py-2 border-t border-b my-2">
                <span className="text-gray-600 text-sm">
                  {pkg.isActive ? 'Available' : 'Unavailable'}
                </span>
                <div
                  onClick={() => handleStatusUpdate(pkg._id, !pkg.isActive)}
                  className={`relative inline-block w-12 h-6 rounded-full cursor-pointer transition-colors ${
                    pkg.isActive ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0 left-0 w-6 h-6 bg-white rounded-full shadow-md transform transition-transform ${
                      pkg.isActive ? 'translate-x-6' : 'translate-x-0'
                    }`}
                  ></span>
                </div>
              </div>
            )}

            {/* Features */}
            <div
              className="rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 
                  border border-gray-100 p-4 mb-4"
            >
              <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <i className="fa-solid fa-list-check text-indigo-600"></i>
                Features
              </p>

              <ul className="space-y-2">
                {pkg?.features?.map((feature, index) => (
                  <li
                    key={index}
                    className="flex items-center text-gray-700 text-sm"
                  >
                    <i className="fa-solid fa-check text-green-500 mr-2"></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex gap-2 mt-auto">
              {user?.userRole._id === SUPER_ADMIN ? (
                <>
                  <button
                    className="flex-1 bg-themeGradient text-white py-2 rounded-lg font-medium hover:opacity-90"
                    onClick={() => {
                      setSelectedPkg(pkg);
                      toggleModal();
                    }}
                  >
                    <i className="fa-solid fa-pen mr-2"></i> Update
                  </button>
                  <button
                    className="flex-1 bg-red-500 text-white py-2 rounded-lg font-medium hover:bg-red-600"
                    onClick={() => handleDelete(pkg._id)}
                  >
                    <i className="fa-solid fa-trash-can mr-2"></i> Delete
                  </button>
                </>
              ) : (
                <button
                  className={`w-full py-2 rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed uppercase text-[16px] tracking-wide
                                ${
                                  subScriptionStatus === 'ACTIVE'
                                    ? 'bg-green-600 text-white'
                                    : subScriptionStatus === 'INCOMPLETE'
                                    ? 'bg-yellow-500 text-black'
                                    : 'bg-themeGradient text-white'
                                }
                          `}
                  onClick={() => {
                    if (subScriptionStatus === 'ACTIVE')
                      return toast.info(
                        'You have already subscribed this plan'
                      );
                    setLoading(pkg._id);
                    handleSubscribe(pkg.planId).finally(() => setLoading(null));
                  }}
                  disabled={loading === pkg._id || hasActiveSubscription}
                >
                  {loading === pkg._id
                    ? 'Processing...'
                    : hasActiveSubscription
                    ? 'Already Subscribed'
                    : subScriptionStatus === 'CANCELED'
                    ? 'Subscribe Plan'
                    : subScriptionStatus || 'Subscribe Plan'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuperPackages;
